/**
 * Presentation model for the AIS "Observations" panel section — what a
 * tip-and-cue task is watching for, and which of the vessels on the map answer
 * it.
 *
 * SHARED, and by the backend's own argument. `operators-service/ais/index.js`:
 * "because every AIS observation is offered to every AIS tipping sensor, there
 * is exactly ONE implementation of each comparison — no vendor can hold its own
 * opinion about what 'MMSI equals' means, and that is enforced by there being
 * nowhere to put one." The same has to be true on this side, or the panel and
 * the engine could disagree per vendor. Compare `vessel-search.model.ts`, which
 * stays Voyage Radar's precisely because every rule in it is a product choice a
 * second provider would be entitled to make differently. Nothing here is: these
 * are facts about AIS data and the observation catalogue.
 *
 * THE POINT OF THIS FILE. On a tip-and-cue task the observation is the TRIGGER:
 * `eetaskapi/operators-service/ais/tipEngine.js` sweeps AIS inside the tasked
 * area and the first vessel matching it opens the cueing sensor's window. The
 * panel therefore has to answer the same question the engine answers, and the
 * matchers below deliberately mirror `operators-service/ais/predicates/` rule
 * for rule — digits compared as strings, the ordering action ignored on an
 * identifier, name equality by default and `contains` only when asked, a ring
 * by point-in-polygon and a lone coordinate by a tolerance radius. Where the
 * two disagree the panel is wrong, so a rule change upstream belongs here too.
 *
 * What it CANNOT answer is whether the task has actually tipped: that verdict
 * is written to `Task.observationMatch`, and this screen reads the Collection
 * document, which does not carry it. So this list says "is it in the snapshot
 * you are looking at", never "did it tip" — and the copy in the component is
 * careful to keep that distinction.
 *
 * Pure: no `@angular/*`, no `leaflet`, no `this` (rule R1). Both feature
 * spellings are tolerated on every read — the host hands over RAW provider
 * features on a live pull and MAPPED ones on a history pick, exactly as
 * `indexLiveVessels` and `searchLoadedVessels` already have to — and that
 * tolerance is also what lets one matcher serve Voyage Radar's `mmsi`/`name`
 * and Spire's `MMSI`/`Name` without a per-vendor adapter.
 */

/** Which predicate an observation dispatches on — the engine's `key`, narrowed. */
export type AisObservationKind = 'mmsi' | 'imo' | 'name' | 'location' | 'other';

/** One observation as the header and body render it. */
export interface AisObservationView {
  /** Stable across re-reads of the same record — the template's trackBy. */
  id: string;
  kind: AisObservationKind;
  /** The chip's small prefix: "MMSI", "IMO", "Name", "Area", "Point". */
  kindLabel: string;
  /** The chip's value: "636092371", "ATLANTIC PIONEER", "4 corners". */
  valueLabel: string;
  /** Full, untruncated value for the chip's `title`. */
  valueTitle: string;
  /** One sentence naming what tips the task. */
  meaning: string;
  /** Ring of `[lng, lat]`, closed, for a location observation over an area. */
  ring: Array<[number, number]> | null;
  /** `[lng, lat]` for a location observation over a single place. */
  point: [number, number] | null;
  /** True when there is a geometry this observation can draw. */
  hasGeometry: boolean;
  /** The raw observation record — what the outputs emit. */
  data: any;
}

/** One vessel on the map that answers an observation. */
export interface AisObservationMatch {
  mmsi: string;
  /** Broadcast name, or null — bbox feeds carry one about half the time. */
  name: string | null;
  label: string;
  isNamed: boolean;
  shipType: string | null;
  lat: number | null;
  lng: number | null;
  /** "12.4 kn", or null when the feed reported no speed. */
  speed: string | null;
  /** "11:54 UTC", or null without a usable stamp. */
  time: string | null;
  /** The feature itself — the host needs it to centre the map. */
  feature: any;
}

/**
 * Metres inside which a vessel counts as "at" a single-point observation.
 * The engine's own default (`config.voyageRadarLocationToleranceMeters` falls
 * back to the same number in `predicates/location.js`).
 */
export const AIS_OBSERVATION_TOLERANCE_M = 500;

/** MMSI is 9 digits, IMO is 7 — the engine refuses anything else outright. */
const IDENTIFIER_DIGITS: { [k in 'mmsi' | 'imo']: number } = { mmsi: 9, imo: 7 };

/** Opt-in substring matching for a name, exactly the engine's set. */
const CONTAINS_ACTIONS = new Set(['contains', 'includes', 'like']);

const str = (v: any): string => (v == null ? '' : String(v));

/** Digits only, compared as a STRING — `Number()` eats an MMSI's leading zero. */
function toDigits(v: any): string {
  return str(v).replace(/\D/g, '');
}

/** Trim → collapse whitespace → upper-case, on both sides of a name compare. */
function normalizeName(v: any): string {
  return str(v).trim().replace(/\s+/g, ' ').toUpperCase();
}

function num(v: any): number | null {
  if (v === '' || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Every property read tolerates the mapped key and the raw feed key. */
function props(feature: any): any {
  return feature?.properties || feature || {};
}

function featureMmsi(feature: any): string {
  const p = props(feature);
  return str(p['MMSI'] ?? p.mmsi).trim();
}

function featureName(feature: any): string | null {
  const p = props(feature);
  const name = str(p['Name'] ?? p.name).trim();
  return name && name !== 'N/A' ? name : null;
}

function featureImo(feature: any): string {
  const p = props(feature);
  return toDigits(p['IMO'] ?? p.imo);
}

function featureShipType(feature: any): string | null {
  const p = props(feature);
  const type = str(p['Ship Type'] ?? p.shipType).trim();
  return type && type !== 'N/A' ? type : null;
}

/** `[lng, lat]` from the geometry, falling back to the flat fields. */
function featureLngLat(feature: any): [number | null, number | null] {
  const coords = feature?.geometry?.coordinates;
  if (Array.isArray(coords) && coords.length >= 2) {
    return [num(coords[0]), num(coords[1])];
  }
  const p = props(feature);
  return [num(p['Lng'] ?? p.longitude), num(p['Lat'] ?? p.latitude)];
}

function featureSpeed(feature: any): string | null {
  const p = props(feature);
  const speed = num(p['Speed'] ?? p.sog);
  return speed == null ? null : `${Math.round(speed * 10) / 10} kn`;
}

/** "11:54 UTC" — the instant the vessel reported, not the snapshot's own name. */
function featureTime(feature: any): string | null {
  const p = props(feature);
  const ms = new Date(str(p['Timestamp UTC'] ?? p.time_utc ?? p.timestamp)).getTime();
  if (isNaN(ms)) return null;
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
}

/**
 * `;`-separated `lng,lat` pairs — the grammar the observation dialog produces
 * (`filter-popup.component.ts` validates exactly this). Malformed pairs are
 * DROPPED rather than thrown on, so one typo degrades to "no match" and never
 * to a blank panel.
 */
function parseCoordPairs(value: string): Array<[number, number]> {
  return str(value)
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((pair) => pair.split(',').map((n) => Number(n.trim())))
    .filter(
      ([lng, lat]) =>
        Number.isFinite(lng) &&
        Number.isFinite(lat) &&
        Math.abs(lat) <= 90 &&
        Math.abs(lng) <= 180
    )
    .map(([lng, lat]) => [lng, lat] as [number, number]);
}

/** Repeats the first vertex when the ring does not already close itself. */
function closeRing(pairs: Array<[number, number]>): Array<[number, number]> {
  const first = pairs[0];
  const last = pairs[pairs.length - 1];
  if (first[0] === last[0] && first[1] === last[1]) return pairs;
  return [...pairs, first];
}

/** Ray casting on the closed ring — the polygon half of `predicates/location.js`. */
function pointInRing(lng: number, lat: number, ring: Array<[number, number]>): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const straddles = yi > lat !== yj > lat;
    if (straddles && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

/** Great-circle metres — only ever used against the tolerance radius. */
function metresBetween(aLng: number, aLat: number, bLng: number, bLat: number): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * The predicate this observation dispatches on.
 *
 * `key` first, because that is the ONLY thing the engine dispatches on and a
 * rename in ee-admin must not move the panel. The name fallback is for the
 * legacy rows that predate the field — a task ordered before the migration
 * carries a keyless snapshot, and it still deserves a readable panel even
 * though it can no longer tip.
 */
export function aisObservationKind(observation: any): AisObservationKind {
  const key = str(observation?.key).trim().toLowerCase();
  if (key === 'vessel_mmsi') return 'mmsi';
  if (key === 'vessel_imo') return 'imo';
  if (key === 'vessel_name') return 'name';
  if (key === 'vessel_location') return 'location';
  if (key) return 'other';

  const name = str(observation?.observationName ?? observation?.name)
    .trim()
    .toLowerCase();
  if (name === 'vessel mmsi') return 'mmsi';
  if (name === 'vessel imo') return 'imo';
  if (name === 'vessel name') return 'name';
  if (name === 'vessel location') return 'location';
  return 'other';
}

/** The value the task was ordered with, as stored on the observation record. */
export function aisObservationValue(observation: any): string {
  return str(observation?.observationValue ?? observation?.value).trim();
}

/**
 * The chip for a location observation whose value PARSED — a single place, or
 * a ring. Never called for the unusable cases (nothing parsed, or the
 * two-coordinate fault), which have no shape to name and would index an empty
 * array here.
 */
function locationLabels(
  pairs: Array<[number, number]>
): { kindLabel: string; valueLabel: string } {
  if (pairs.length === 1) {
    const [lng, lat] = pairs[0];
    return {
      kindLabel: 'Point',
      valueLabel: `${lat.toFixed(3)}, ${lng.toFixed(3)}`,
    };
  }
  // A closed ring stores its first vertex twice; corners are what a reader
  // counts, so the repeat is not one of them.
  const corners = closeRing(pairs).length - 1;
  return { kindLabel: 'Area', valueLabel: `${corners} corners` };
}

/**
 * One observation record → what the header chip and the hint line render.
 *
 * Returns null for a record with no usable value: an observation the task
 * cannot be watching for is not a section, and a chip reading "undefined" is
 * worse than nothing.
 */
export function buildAisObservation(
  observation: any,
  index = 0
): AisObservationView | null {
  const value = aisObservationValue(observation);
  if (!value) return null;

  const kind = aisObservationKind(observation);
  const name = str(observation?.observationName ?? observation?.name).trim();
  const id = str(observation?._id ?? observation?.key ?? `${name}-${index}`);
  const action = str(observation?.action ?? observation?.operator).trim().toLowerCase();

  let kindLabel = name || 'Observation';
  let valueLabel = value;
  let meaning = 'Tips the task when this observation is met inside the target.';
  let ring: Array<[number, number]> | null = null;
  let point: [number, number] | null = null;

  if (kind === 'mmsi' || kind === 'imo') {
    const digits = toDigits(value);
    const wanted = IDENTIFIER_DIGITS[kind];
    kindLabel = kind.toUpperCase();
    valueLabel = digits || value;
    meaning =
      digits.length === wanted
        ? `Tips the task when a vessel reporting this ${kindLabel} is seen inside the target.`
        : `This ${kindLabel} is ${digits.length} digits — the task needs ${wanted}, so nothing can match it.`;
    if (kind === 'imo' && digits.length === wanted) {
      meaning += ' Most AIS position reports carry no IMO.';
    }
  } else if (kind === 'name') {
    kindLabel = 'Name';
    valueLabel = value;
    meaning = CONTAINS_ACTIONS.has(action)
      ? 'Tips the task when a vessel whose name contains this is seen inside the target.'
      : 'Tips the task when a vessel whose name matches this exactly is seen inside the target.';
  } else if (kind === 'location') {
    const pairs = parseCoordPairs(value);
    // Falls through to the raw value on both unusable shapes: a chip has to say
    // SOMETHING, and the stored string is the only honest thing left to show.
    kindLabel = 'Location';
    if (pairs.length === 1) {
      point = pairs[0];
      meaning = `Tips the task when any vessel comes within ${AIS_OBSERVATION_TOLERANCE_M} m of this place.`;
    } else if (pairs.length >= 3) {
      ring = closeRing(pairs);
      meaning = 'Tips the task when any vessel is seen inside this area.';
    } else if (pairs.length === 2) {
      // Two coordinates describe neither a place nor an area, and the dialog
      // cannot produce one — named rather than silently drawn as a line.
      meaning =
        'This location has 2 coordinates — it needs 1 for a place or 3 or more for an area, so nothing can match it.';
    } else {
      meaning = 'This location has no usable coordinates, so nothing can match it.';
    }
    if (point || ring) {
      const labels = locationLabels(pairs);
      kindLabel = labels.kindLabel;
      valueLabel = labels.valueLabel;
    }
  }

  return {
    id,
    kind,
    kindLabel,
    valueLabel,
    valueTitle: value,
    meaning,
    ring,
    point,
    hasGeometry: !!(ring || point),
    data: observation,
  };
}

/** Every observation on the sensor that can be rendered, in stored order. */
export function buildAisObservations(observations: any): AisObservationView[] {
  return (Array.isArray(observations) ? observations : [])
    .map((o, i) => buildAisObservation(o, i))
    .filter((v): v is AisObservationView => v !== null);
}

/** One matched feature → the row the body renders. */
function toMatch(feature: any): AisObservationMatch {
  const mmsi = featureMmsi(feature);
  const name = featureName(feature);
  const [lng, lat] = featureLngLat(feature);
  return {
    mmsi,
    name,
    label: name || (mmsi ? `MMSI ${mmsi}` : 'Unnamed vessel'),
    isNamed: !!name,
    shipType: featureShipType(feature),
    lat,
    lng,
    speed: featureSpeed(feature),
    time: featureTime(feature),
    feature,
  };
}

/**
 * The vessels on the map that answer this observation.
 *
 * Deliberately searches only what is DRAWN — the host passes the snapshot (or
 * playback frame) currently on screen, so stepping through Refresh History
 * re-answers the question per snapshot and the list always describes what the
 * user can see. Searching every snapshot loaded this session would answer a
 * different question ("was it ever here") with rows pointing at nothing.
 *
 * De-duplicated by MMSI: a feed can report one hull twice in a pull, and the
 * first report wins so the row is stable while the list is re-derived.
 */
export function matchAisObservation(
  view: AisObservationView,
  features: any[]
): AisObservationMatch[] {
  const list = Array.isArray(features) ? features : [];
  if (!list.length) return [];

  const matched: AisObservationMatch[] = [];
  const seen = new Set<string>();
  const keep = (feature: any) => {
    const mmsi = featureMmsi(feature);
    const dedupe = mmsi || `#${matched.length}`;
    if (seen.has(dedupe)) return;
    seen.add(dedupe);
    matched.push(toMatch(feature));
  };

  if (view.kind === 'mmsi' || view.kind === 'imo') {
    const wanted = toDigits(view.valueTitle);
    // Refused rather than compared, like the engine: a 7-digit value in an MMSI
    // observation is a mis-entered IMO, and matching it against everything
    // would be worse than matching it against nothing.
    if (wanted.length !== IDENTIFIER_DIGITS[view.kind]) return [];
    for (const f of list) {
      const got = view.kind === 'mmsi' ? featureMmsi(f) : featureImo(f);
      // A vessel missing the field is a NON-match, never an error.
      if (got && toDigits(got) === wanted) keep(f);
    }
    return matched;
  }

  if (view.kind === 'name') {
    const wanted = normalizeName(view.valueTitle);
    if (wanted.length < 2) return [];
    const action = str(view.data?.action ?? view.data?.operator).trim().toLowerCase();
    const useContains = CONTAINS_ACTIONS.has(action);
    for (const f of list) {
      const name = normalizeName(featureName(f));
      if (!name) continue;
      if (name === wanted || (useContains && name.includes(wanted))) keep(f);
    }
    return matched;
  }

  if (view.kind === 'location') {
    if (view.point) {
      const [lng, lat] = view.point;
      for (const f of list) {
        const [fLng, fLat] = featureLngLat(f);
        if (fLng == null || fLat == null) continue;
        if (metresBetween(lng, lat, fLng, fLat) <= AIS_OBSERVATION_TOLERANCE_M) {
          keep(f);
        }
      }
      return matched;
    }
    if (view.ring) {
      for (const f of list) {
        const [fLng, fLat] = featureLngLat(f);
        if (fLng == null || fLat == null) continue;
        if (pointInRing(fLng, fLat, view.ring)) keep(f);
      }
    }
    return matched;
  }

  // An observation this build has no predicate for — a feed-based row on a
  // Voyage Radar sensor, or a legacy record naming something since renamed.
  // Falls back to the walk the host has always done for those: any property
  // equal to the value. Loose, and knowingly so; it is the only honest answer
  // available when the rule itself is unknown.
  const wanted = str(view.valueTitle).trim();
  for (const f of list) {
    const p = props(f);
    if (Object.keys(p).some((k) => str(p[k]).trim() === wanted)) keep(f);
  }
  return matched;
}

/**
 * The features a task's observations answer to — the union of every view's
 * matches, in the order the snapshot listed them.
 *
 * This is what "Observations is on the map" DRAWS. The section itself only ever
 * needed the matches as rows, so the matcher above hands back `AisObservationMatch`
 * records; a host that draws them needs the raw features back, and needs the
 * union rather than one view's set — a task with two observations puts both
 * answers on one map.
 *
 * De-duplicated by feature IDENTITY, not by MMSI: two observations can name the
 * same hull (an MMSI and the area it is sitting in), and drawing that vessel
 * twice would stack two markers and two popups on one position. Identity is the
 * right key because every view matched against the SAME array, so the same
 * vessel is the same object.
 *
 * Pure, like everything else here — the caller owns what a "feature" is drawn as.
 */
export function matchedAisObservationFeatures(
  views: AisObservationView[],
  features: any[]
): any[] {
  const list = Array.isArray(features) ? features : [];
  if (!list.length) return [];
  const seen = new Set<any>();
  const out: any[] = [];
  for (const view of Array.isArray(views) ? views : []) {
    for (const match of matchAisObservation(view, list)) {
      const feature = match.feature;
      if (!feature || seen.has(feature)) continue;
      seen.add(feature);
      out.push(feature);
    }
  }
  return out;
}
